import React, { useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { Col } from 'reactstrap';
import PropTypes from 'prop-types';
import { theme } from 'styled-tools';
import styled from 'styled-components/macro';
import { FormRow, ProfileForm } from 'routes/Profile';
import Button from 'components/Button';
import { CheckboxInput, ErrorMessage, FieldLabel, InputWrapper, StyledInput } from 'components/Form';
import MobileNumberVerificationWrapper from 'components/MobileNumber/MobileNumberVerificationWrapper';
import { isValidMobileNumber } from 'utils/mobileNumberValidation';
import { useMobileNumberVerification } from 'hooks/useMobileNumberVerification/useMobileNumberVerification';
import { mobileOptinText } from 'components/MobileNumber/const';
import ConfirmedUnconfirmed from 'components/MobileNumber/confirmedUnconfirmed';
import MobileNumberVerificationStatus from 'components/MobileNumber/MobileNumberVerificationStatus';

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
`;

const Cancel = styled.button`
  border: none;
  padding: 0;
  text-decoration: underline;
  background: none;
  color: ${theme('colors.primary')};

  &:hover {
    background: transparent;
    border: none;
    text-decoration: underline;
    color: ${theme('colors.black')};
  }

  &:focus,
  &:visited,
  &:active {
    color: inherit;
    outline: none;
    box-shadow: none;
  }
`;

const MobileBenefitsHeading = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.75rem;
`;

const MobileBenefitsList = styled.ul`
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.4;
  margin: 0;

  li {
    margin-bottom: 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const mobileNumberBenefits = [
  'Quickly sign in without a password or reset your password with a one‑time code',
  'Get notified about important account activity',
  '(Optional) Receive exclusive tips, invites, and updates on free webinars and training',
];

function validate(values) {
  const errors = {};

  if (values.mobileNumber && !isValidMobileNumber(values.mobileNumber)) {
    errors.mobileNumber = ' ';
  }

  return errors;
}

function PhoneNumberEntryForm({ onSubmit, onCancel, loading, setAlert }) {
  const [form, setForm] = useState(null);
  const {
    showVerificationModal,
    setShowVerificationModal,
    mobileNumberToVerify,
    isMobileNumberVerified,
    setIsMobileNumberVerified,
    mobileNumbersMatch,
    handleSendVerificationCode,
    errorVerification,
    currentValues,
    setErrorVerification,
    isMobileNumberValid,
    sendMobileVerificationCodeLoading,
  } = useMobileNumberVerification(form);

  useEffect(() => {
    if (errorVerification) {
      setAlert({ type: 'error', message: errorVerification });
    } else {
      setAlert(null);
    }
  }, [errorVerification, setAlert]);

  // Clear verification error when verification completes
  useEffect(() => {
    if (isMobileNumberVerified) {
      setAlert(null);
    }
  }, [isMobileNumberVerified, setAlert]);

  const handleSubmit = async (values, isMobileNumberVerified) => {
    if (!isMobileNumberVerified) {
      return;
    }
    onSubmit(values, isMobileNumberVerified);
  };

  return (
    <Form
      onSubmit={values => handleSubmit(values, isMobileNumberVerified)}
      validate={validate}
      render={({ form: formApi, handleSubmit }) => {
        if (!form) {
          setForm(formApi);
        }

        return (
          <ProfileForm onSubmit={handleSubmit}>
            <FormRow style={{ marginBottom: '0' }}>
              <Col xs='12' md={isMobileNumberVerified ? 12 : 6}>
                <Field name='mobileNumber' type='text'>
                  {({ input, meta }) => {
                    const handleChange = e => {
                      input.onChange(e);
                      setIsMobileNumberVerified(false);
                    };

                    return (
                      <InputWrapper>
                        <FieldLabel htmlFor='mobileNumber' $flexSpace>
                          Mobile Number
                          {input.value.length > 0 && (
                            <ConfirmedUnconfirmed
                              mobileNumber={input.value}
                              isMobileNumberVerified={isMobileNumberVerified}
                            />
                          )}
                          <MobileNumberVerificationWrapper
                            form={form}
                            isMobileNumberValid={isMobileNumberValid}
                            isMobileNumberVerified={isMobileNumberVerified}
                            setIsMobileNumberVerified={setIsMobileNumberVerified}
                            showVerificationModal={showVerificationModal}
                            setShowVerificationModal={setShowVerificationModal}
                            mobileNumberToVerify={mobileNumberToVerify}
                            handleSendVerificationCode={handleSendVerificationCode}
                            sendMobileVerificationCodeLoading={sendMobileVerificationCodeLoading}
                            currentValues={currentValues}
                            setErrorVerification={setErrorVerification}
                          />
                        </FieldLabel>

                        <StyledInput
                          {...input}
                          onChange={handleChange}
                          id='mobileNumber'
                          invalid={meta.error && meta.touched}
                          placeholder='Mobile Phone'
                          className={meta.error && meta.touched && 'input-invalid'}
                          autoComplete='new-no'
                        />

                        {meta.error && meta.touched && <ErrorMessage>{meta.error}</ErrorMessage>}
                      </InputWrapper>
                    );
                  }}
                </Field>
              </Col>
              {isMobileNumberVerified ? (
                <></>
              ) : (
                <Col xs='12' md='6'>
                  <FieldLabel style={{ marginTop: '1.4rem', width: '100%' }}>
                    <MobileNumberVerificationStatus
                      isMobileNumberVerified={isMobileNumberVerified}
                      isMobileNumberValid={isMobileNumberValid}
                      onSendVerificationCode={handleSendVerificationCode}
                      isLoading={sendMobileVerificationCodeLoading}
                      currentValues={currentValues}
                      setErrorVerification={setErrorVerification}
                    />
                  </FieldLabel>
                </Col>
              )}
            </FormRow>
            <FormRow style={{ margin: '1rem 0' }}>
              <Col xs={12}>
                <MobileBenefitsHeading>Why add your number?</MobileBenefitsHeading>
                <MobileBenefitsList>
                  {mobileNumberBenefits.map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                </MobileBenefitsList>
              </Col>
            </FormRow>
            <FormRow>
              <Col>
                <CheckboxInput name='smsOptIn' style={{ width: '1.25rem' }}>
                  {mobileOptinText}
                </CheckboxInput>
              </Col>
            </FormRow>
            <ButtonContainer>
              <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                <Cancel type='button' onClick={() => onCancel(true)}>
                  Remind Me Later
                </Cancel>
              </div>
              <Button style={{ width: '50%' }} type='submit' disabled={!isMobileNumberVerified}>
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </ButtonContainer>
          </ProfileForm>
        );
      }}
    />
  );
}

PhoneNumberEntryForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  setAlert: PropTypes.func.isRequired,
};

export default PhoneNumberEntryForm;
